import { useCallback, useEffect, useRef, useState } from "react";

import {
  Button,
  getUserIdFromRefreshToken,
  Spinner,
  SpinnerCenter,
  VideoStarted,
} from "@shapr3d/frontend-lib";
import { useQuery } from "@tanstack/react-query";
import { MessageDescriptor } from "react-intl";

import print3DIllustration from "../../assets/images/intentPage/3DPrint.webp";
import communicateIllustration from "../../assets/images/intentPage/communicate.webp";
import fixOnTheSpotIllustration from "../../assets/images/intentPage/fixOnTheSpot.webp";
import modelAnywhereIllustration from "../../assets/images/intentPage/modelAnywhere.webp";
import renderIllustration from "../../assets/images/intentPage/render.webp";
import reviewInARIllustration from "../../assets/images/intentPage/reviewInAR.webp";
import { localStorageKeys } from "../../config";
import useNavigation from "../../hooks/navigation";
import { useSetAppProperties } from "../../hooks/useGetAppProperties";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import messages from "../../locales/messages";
import Heading from "../Heading/Heading";
import { OverlayPlayer, OverlayPlayerHandle } from "../VimeoVideo/VimeoVideo";

import style from "./IntentPage.module.css";

type Video = {
  id: string;
  telemetryID: VideoStarted["eventProperties"]["video_id"];
  title: MessageDescriptor;
  thumbUrl: string;
  description: MessageDescriptor;
};

const videosV2 = [
  {
    id: "1144496905",
    telemetryID: "model_parts",
    title: messages.IntentPage.Videos.ModelParts.Title,
    description: messages.IntentPage.Videos.ModelParts.Description,
    thumbUrl: print3DIllustration,
  },
  {
    id: "1144496887",
    telemetryID: "model_anywhere",
    title: messages.IntentPage.Videos.ModelAnywhere.Title,
    description: messages.IntentPage.Videos.ModelAnywhere.Description,
    thumbUrl: modelAnywhereIllustration,
  },
  {
    id: "1144496780",
    telemetryID: "communicate_design_intent",
    title: messages.IntentPage.Videos.CommunicateDesignIntent.Title,
    description: messages.IntentPage.Videos.CommunicateDesignIntent.Description,
    thumbUrl: communicateIllustration,
  },
  {
    id: "1144496929",
    telemetryID: "review_edit_ar",
    title: messages.IntentPage.Videos.ReviewEditAR.Title,
    description: messages.IntentPage.Videos.ReviewEditAR.Description,
    thumbUrl: reviewInARIllustration,
  },
  {
    id: "1144496867",
    telemetryID: "generative_render",
    title: messages.IntentPage.Videos.GenerativeRender.Title,
    description: messages.IntentPage.Videos.GenerativeRender.Description,
    thumbUrl: renderIllustration,
  },
  {
    id: "1144496826",
    telemetryID: "fix_cad_import",
    title: messages.IntentPage.Videos.FixCADImport.Title,
    description: messages.IntentPage.Videos.FixCADImport.Description,
    thumbUrl: fixOnTheSpotIllustration,
  },
] satisfies Video[];

function formatVideoDuration(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds) % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

const Card = ({
  onClick,
  thumbUrl,
  title,
  description,
  videoId,
}: {
  onClick: () => void;
  thumbUrl: string;
  title: MessageDescriptor;
  description: MessageDescriptor;
  videoId: string;
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={style.card}
      onClick={() => {
        onClick();
      }}
    >
      <Thumbnail src={thumbUrl} alt={t(title)} videoId={videoId} />
      <div>
        <div className={style.videoTitle}>{t(title)}</div>
        <div className={style.videoDescription}>{t(description)}</div>
      </div>
    </div>
  );
};

const Thumbnail: React.FC<React.ImgHTMLAttributes<HTMLImageElement> & { videoId: string }> = (
  props
) => {
  const { data, isLoading } = useQuery({
    queryKey: ["videoMeta", props.videoId],
    queryFn: async () => {
      const res = await fetch(
        `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${props.videoId}`)}`
      );
      if (!res.ok) throw new Error(`oEmbed failed for ${props.videoId}`);
      return res.json();
    },
  });

  return (
    <div
      className={style.imageContainer}
      onClick={(e) => {
        props.onClick?.(e as React.MouseEvent<HTMLImageElement>);
      }}
    >
      {!isLoading && (
        <div className={style.videoLengthLabel}>{formatVideoDuration(data?.duration ?? 0)}</div>
      )}
      <img className={style.thumbnail} src={props.src} alt={props.alt} />
      {isLoading && (
        <div className={style.center}>
          <Spinner />
        </div>
      )}
    </div>
  );
};

const cleanupSeenIntentPageUser = () => {
  const hasSeenIntentPageUserMap = JSON.parse(
    localStorage.getItem(localStorageKeys.hasSeenIntentPageUserMap) ?? "{}"
  );
  delete hasSeenIntentPageUserMap[getUserIdFromRefreshToken() ?? ""];

  localStorage.setItem(
    localStorageKeys.hasSeenIntentPageUserMap,
    JSON.stringify(hasSeenIntentPageUserMap)
  );
};

const useSeenIntentPage = () => {
  const [hasSeenIntentPage] = useState(() => {
    const userID = getUserIdFromRefreshToken() ?? "";
    const hasSeenIntentPageUserMap = JSON.parse(
      localStorage.getItem(localStorageKeys.hasSeenIntentPageUserMap) ?? "{}"
    );

    return hasSeenIntentPageUserMap[userID];
  });
  useEffect(() => {
    const hasSeenIntentPageUserMap = JSON.parse(
      localStorage.getItem(localStorageKeys.hasSeenIntentPageUserMap) ?? "{}"
    );
    hasSeenIntentPageUserMap[getUserIdFromRefreshToken() ?? ""] = true;

    localStorage.setItem(
      localStorageKeys.hasSeenIntentPageUserMap,
      JSON.stringify(hasSeenIntentPageUserMap)
    );
  }, []);

  return hasSeenIntentPage;
};

function VideoPlaylist({ videos, finish }: { videos: Video[]; finish: () => void }) {
  const tracker = useTracker();
  const hasSeenIntentPage = useSeenIntentPage();

  const { t, m } = useTranslation();

  const isOnLoadTracked = useRef(false);

  const playerRef = useRef<OverlayPlayerHandle | null>(null);

  const playNextVideo = (reason: "autoPlay" | "nextClicked") => {
    setActiveVideoID((prevID) => {
      const currentIndex = videos.findIndex((v) => v.id === prevID);
      if (currentIndex === -1) {
        return prevID;
      }
      const nextIndex = (currentIndex + 1) % videos.length;

      tracker.trackVideoStopped({
        reason: reason === "autoPlay" ? "finished" : "next_clicked",
        videoId: videos[currentIndex].telemetryID,
      });
      tracker.trackVideoStarted({
        from: reason === "autoPlay" ? "auto_play" : "clicked_next",
        videoId: videos[nextIndex].telemetryID,
      });

      const newVideoId = videos[nextIndex].id;
      playerRef.current?.loadAndPlay(newVideoId);
      return newVideoId;
    });
  };

  const playPreviousVideo = () => {
    setActiveVideoID((prevID) => {
      const currentIndex = videos.findIndex((v) => v.id === prevID);
      if (currentIndex === -1) {
        return prevID;
      }
      const previousIndex = (currentIndex - 1 + videos.length) % videos.length;

      tracker.trackVideoStopped({
        reason: "previous_clicked",
        videoId: videos[currentIndex].telemetryID,
      });
      tracker.trackVideoStarted({
        from: "clicked_previous",
        videoId: videos[previousIndex].telemetryID,
      });

      const newVideoId = videos[previousIndex].id;
      playerRef.current?.loadAndPlay(newVideoId);
      return newVideoId;
    });
  };

  const [hasAnyVideoPlayed, setHasAnyVideoPlayed] = useState(false);
  const canProgress = hasAnyVideoPlayed || hasSeenIntentPage;

  const [activeVideoID, setActiveVideoID] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const isAnyPopoverVisible = activeVideoID !== null;
  const getTelemetryID = useCallback(
    (videoID: string) => {
      const video = videos.find((v) => v.id === videoID);
      return video?.telemetryID;
    },
    [videos]
  );
  useEffect(() => {
    if (activeVideoID) {
      setHasAnyVideoPlayed(true);
    } else {
      const videoId = getTelemetryID(activeVideoID ?? "");
      if (videoId) {
        tracker.trackVideoStopped({
          reason: "closed",
          videoId,
        });
      }
    }
  }, [activeVideoID, getTelemetryID, tracker]);

  return (
    <>
      <div className={style.playListContainer}>
        {videos.map(({ id, thumbUrl, title, description, telemetryID }) => (
          <div key={id}>
            <Card
              onClick={() => {
                tracker.trackVideoStarted({
                  from: "intent_page",
                  videoId: telemetryID,
                });
                setActiveVideoID(id);
                setIsOpen(true);
                requestAnimationFrame(() => {
                  playerRef.current?.loadAndPlay(id);
                });
              }}
              videoId={id}
              thumbUrl={thumbUrl}
              title={title}
              description={description}
            />
          </div>
        ))}
      </div>
      {canProgress && !isAnyPopoverVisible && (
        <Button
          variant="primary"
          onClick={() => {
            tracker.trackIntentPageFinished({ trigger: "page" });
            finish();
          }}
        >
          {t(m.IntentPage.Buttons.StartModeling)}
        </Button>
      )}
      {!canProgress && !isAnyPopoverVisible && (
        <div className={style.footerNote}>{t(m.IntentPage.WatchNote)}</div>
      )}
      <OverlayPlayer
        ref={playerRef}
        open={isOpen}
        onClose={() => {
          setActiveVideoID(null);
          setIsOpen(false);
        }}
        videoId={activeVideoID ?? videos[0].id}
        onReady={(player) => {
          if (!isOnLoadTracked.current) {
            tracker.trackIntentPageLoaded();
            isOnLoadTracked.current = true;
          }

          player.on("ended", () => {
            playNextVideo("autoPlay");
          });
        }}
      >
        <div className={style.captionBackground}>
          <Button
            variant="grayscale"
            className={style.captionButton}
            onClick={() => {
              playPreviousVideo();
            }}
          >
            {t(m.IntentPage.Buttons.Previous)}
          </Button>
          <Button
            variant="primary"
            className={style.captionButton}
            onClick={() => {
              const videoId = getTelemetryID(activeVideoID ?? "");
              if (videoId) {
                tracker.trackVideoStopped({
                  reason: "start_modeling_clicked",
                  videoId,
                });
              }
              tracker.trackIntentPageFinished({ trigger: "video" });
              finish();
            }}
          >
            {t(m.IntentPage.Buttons.StartModeling)}
          </Button>
          <Button
            variant="grayscale"
            className={style.captionButton}
            onClick={() => {
              playNextVideo("nextClicked");
            }}
          >
            {t(m.IntentPage.Buttons.Next)}
          </Button>
        </div>
      </OverlayPlayer>
    </>
  );
}

export const IntentPage = () => {
  const { t, m } = useTranslation();
  const navigation = useNavigation();

  const { mutateAsync: mutateAppProperties, isIdle } = useSetAppProperties();
  const finishSurvey = () => {
    const finishedAt = new Date().toISOString();

    mutateAppProperties(
      {
        is_resegmentation_needed: false,
        segmentation_work_completed: true,
        segmentation_work_web_completed: true,
        segmentation_is_student_or_teacher: false,
        segmentation_v2_finished_at: finishedAt,
        segmentation_v2_progressive_profiling_finished_at: finishedAt,
      },
      {
        onSuccess: () => {
          cleanupSeenIntentPageUser();
          navigation.tryRedirectFromSearchParams();
        },
      }
    );
  };

  if (!isIdle) {
    return <SpinnerCenter />;
  }

  return (
    <div className={style.pageContainer}>
      <div className={style.title}>
        <Heading type="h1">{t(m.IntentPage.Title)}</Heading>
        <div>{t(m.IntentPage.Subtitle)}</div>
      </div>
      <VideoPlaylist videos={videosV2} finish={finishSurvey} />
    </div>
  );
};
